import React, { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sticker from "../../../components/elements/sticker/Sticker";
import Container from "../../../components/elements/container/Container";

import Tx from "../../../types/naming";
import { ApiSelect, Rotate, TextType } from "@baseTypes/baseType";

import CropsConditionChart from "./chart";
import { stickerLoadingWithCrop } from "../phases";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../store";
import { useTypedSelector } from "@hooks/useTypedSelector";
import st from './styles.module.scss';
import StickerHeader from "../../../components/elements/sticker/StickerHeader";
import { Empty } from "./empty";
import { CropsConditionState } from "../../../store/reducers/dashboard/cropscondition/types";
import { identity } from "fp-ts/es6/function";
import cn from "classnames";
import { formatNumber2d } from "@utils/format/number";
import { isEmpty } from "lodash";
import Legend, { LegendRow } from "@elements/Legend/Legend";
import CropsRowRenderer from "@elements/Legend/components/CropsRowRenderer/CropsRowRenderer";
import { CircleLabel } from "@pages/dashboard/works/detalization/dynamics";

interface CropsDashboardProps {
    baseCropId?: any;
}

export const stateCropSelector: ApiSelect<CropsConditionState> =
    createSelector((state: RootState) => state.dashCropsCondition, identity<CropsConditionState>);

const DiffDownIcon = () =>
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none">
        <path d="M14 12L16.29 9.71L11.41 4.83L7.41 8.83L0 1.41L1.41 0L7.41 6L11.41 2L17.71 8.29L20 6V12H14Z"
              fill="#14780C"/>
    </svg>

const DiffUpIcon = () =>
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none">
        <path d="M14 0L16.29 2.29L11.41 7.17L7.41 3.17L0 10.59L1.41 12L7.41 6L11.41 10L17.71 3.71L20 6V0H14Z"
              fill="#EB3333"/>
    </svg>

const StateCropsDashboard: FC<CropsDashboardProps> = () => {
    const navigate = useNavigate();

    let {
        data
    } = useTypedSelector(stateCropSelector)

    const { selected } = useTypedSelector(state => state.cropSelector);

    const legendRows = useMemo((): LegendRow[] => {
        return data.fieldConditions.map(condition => {
            const diffStatus = condition.dynamics === 0 ? 'stand' : condition.dynamics > 0 ? 'up' : 'down'

            return (
                {
                    id: condition.type,
                    values: [
                        <CircleLabel size={'xl'} align={'start'} color={condition.color} name={condition.type} />,
                        <div className={st['legend-text']}>
                            <span className={cn(st['legend-text__value'], st[`legend-text--${diffStatus}`])}>{formatNumber2d(Math.abs(condition.dynamics))}</span>
                            {diffStatus !== 'stand' && <span>{diffStatus === 'up' ? DiffUpIcon() : DiffDownIcon()}</span>}
                        </div>
                    ],
                    color: condition.color
                }
            )
        })
    }, [ data.fieldConditions ])

    const renderBody = () => <>
        <StickerHeader
            textType={TextType.Header}
            onClick={() => navigate(`crops-states`)}
            isLink={true}
            title={Tx.CropCondition}
            rightPlacement={() => formatNumber2d(data.total) + " Га"}
        />
        <Container>
            <CropsConditionChart data={data} />
            <Legend rows={legendRows}
                    unselectable
                    gap={4}
                    rowVariant={'default'}
                    rotate={Rotate.Column}
                    rowRenderer={row => <CropsRowRenderer row={row}/>} />
        </Container>
    </>
    return (
        <Sticker className={st.sticker}>
            {data.total && selected && !isEmpty(selected) ? renderBody() : <Empty/>}
        </Sticker>
    );
};

export default stickerLoadingWithCrop(
    stateCropSelector,
    actions => actions.fetchCropsConditions
)(StateCropsDashboard);
